// ─── RPC simpan produk ❤ (mig 6270) ─────────────────────────────────────────
//
// SELALU lewat klien ber-sesi PEMBELI (`createBrowserSupabase()` — scope dari
// path; pemanggilnya `SavedProductsProvider` yang hanya hidup di jalur
// storefront, jadi scope-nya selalu sesi pembeli), tak pernah klien anon
// `@/lib/supabase/client`: EXECUTE ketiga RPC hanya untuk `authenticated`
// (idiom `preorderVariant.ts` — klien anon mengirim JWT anon walau pembelinya
// sudah login, dan ditolak 42501). Scope "store" sengaja TIDAK ditulis
// eksplisit: di `src/lib/supabase` itu penanda modul yang berjalan sebagai
// anon dan wajib memfilter store_id sendiri (`storeScopedQueries.test.ts`).
//
// Tabel `saved_products` deny-all, jadi modul ini tak punya `.from()` sama
// sekali. Toko tak pernah dikirim dari sini: RPC mengambilnya dari sesi
// (`coalesce(current_store_id(), service_store_context())`, tolak NULL) —
// tak ada parameter toko yang bisa ditempa.

import { createBrowserSupabase } from "@/lib/supabase/browser";
import type { SavedProductRow } from "@/lib/savedProducts";
import { withTimeout } from "@/lib/withTimeout";

// Cermin `RESOLVE_TIMEOUT_MS` preorderVariant.ts: tombol ❤ tak boleh menggantung.
const SAVED_RPC_TIMEOUT_MS = 8000;

/** Kegagalan RPC dengan SQLSTATE-nya (`classifySavedFailure`). */
export class SavedProductsError extends Error {
  readonly code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = "SavedProductsError";
    this.code = code;
  }
}

function db() {
  const client = createBrowserSupabase();
  if (!client) throw new SavedProductsError("Supabase belum dikonfigurasi.", null);
  return client;
}

function fail(error: { message: string; code?: string | null }): never {
  throw new SavedProductsError(error.message, error.code ?? null);
}

/** Daftar milik pembeli yang login — terbaru dulu, produk aktif saja. */
export async function fetchMySavedProducts(): Promise<SavedProductRow[]> {
  const { data, error } = await withTimeout(db().rpc("list_my_saved_products"), SAVED_RPC_TIMEOUT_MS);
  if (error) fail(error);
  return ((data ?? []) as { product_id: string; saved_at: string }[]).map((row) => ({
    productId: row.product_id,
    savedAt: row.saved_at,
  }));
}

/** Simpan (`true`) / lepas (`false`); memulangkan keadaan akhir. */
export async function setProductSaved(productId: string, saved: boolean): Promise<boolean> {
  const { data, error } = await withTimeout(
    db().rpc("set_product_saved", { p_product_id: productId, p_saved: saved }),
    SAVED_RPC_TIMEOUT_MS
  );
  if (error) fail(error);
  return data === true;
}

/**
 * Gabung simpanan tamu dalam SATU panggilan (≤ 500 id sejak mig 6290, terbaru
 * dulu); memulangkan jumlah yang ditambahkan.
 */
export async function saveProductsBulk(productIds: string[]): Promise<number> {
  if (!productIds.length) return 0;
  const { data, error } = await withTimeout(
    db().rpc("save_products_bulk", { p_product_ids: productIds }),
    SAVED_RPC_TIMEOUT_MS
  );
  if (error) fail(error);
  return typeof data === "number" ? data : 0;
}
